'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildAttractionUrl,
  type AttractionSummary,
  type SelectedDestination,
} from '../config';
import { cityRegionLine, type AttractionCityRecord } from '../_data/attractions.api';
import { suggestAttractions, suggestCities } from '../_data/catalogue';
import heroImage from '../_assets/attractions-hero.jpg';
import { PinIcon, SearchIcon } from './Icons';
import { SearchCombobox, type ComboOption } from './SearchCombobox';
import { useTenantBrand } from '../../_components/TenantBrandProvider';
import { heroFor } from '../../_lib/tenant/hero';

interface AttractionsHeroProps {
  /**
   * The destination the page resolved, id included.
   *
   * Passed whole rather than as a name so the hero starts out holding the
   * SAME canonical identity the listing was built from — reconstructing it
   * from the display text is what allowed the field and the results to drift
   * apart for same-named cities.
   */
  initialDestination?: SelectedDestination | null;
  /**
   * Raw Location text to seed the input with when it resolved to no
   * destination.
   *
   * Display only — it pins nothing, so the attraction field stays blocked and
   * nothing downstream treats it as a city. It exists so a visitor who typed
   * something we could not match still sees what they typed and can correct it,
   * rather than finding the box wiped.
   */
  initialLocationText?: string;
  initialQuery?: string;
}

/**
 * Rows in either dropdown (BCP-2056 §4; BCP-2055 §4 leaves it unstated and its
 * note says to assume the same five).
 */
const SUGGESTION_LIMIT = 5;
/** Suggestions appear only from the third character (BCP-2055 §4, BCP-2056 §4). */
const MIN_QUERY_LEN = 3;
/** Debounce before hitting the API, ms. */
const DEBOUNCE_MS = 250;
/** One string for both fields — the two tickets spell it differently (2055 §4 note). */
const NO_RESULTS = 'No results found';

const sameCity = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Shown on the Location field when the attraction field is used before it. */
const CITY_REQUIRED = 'City is required';

/**
 * Listing hero — the ticket-shaped image panel with the pill search bar
 * (`home-page.png`).
 *
 * Both fields are comboboxes over the live catalogue:
 *   Location   — tier-ranked destinations from the service, debounced. Rows
 *                read "London" over "United Kingdom", matching the hotels
 *                destination dropdown.
 *   Attraction — a debounced query against the service, SCOPED to the chosen
 *                destination, so picking London offers London products only;
 *                picking one goes straight to it.
 *
 * The two fields still write to the URL (`?city=` / `?q=`) rather than to
 * component state, so a search survives back-navigation and can be linked to,
 * matching the "search state lives in the URL" rule the other domains follow.
 */
export function AttractionsHero({
  initialDestination = null,
  initialLocationText = '',
  initialQuery = '',
}: AttractionsHeroProps) {
  const tenantHero = heroFor(useTenantBrand(), 'attractions', heroImage.src);
  const router = useRouter();
  const [location, setLocation] = useState(initialDestination?.city ?? initialLocationText);
  const [query, setQuery] = useState(initialQuery);

  /**
   * The destination currently in force, seeded from the listing's own resolved
   * city so the attraction field is scoped on arrival — before that first load
   * the page has already picked a city (from `?city=`, geo, or the London
   * fallback), and the visitor would reasonably expect the name search to
   * respect the results they are looking at.
   *
   * Cleared the moment the visitor edits the field away from it: half-typed
   * text names no destination, so scoping to it would silently return nothing.
   */
  const [selectedCity, setSelectedCity] = useState<SelectedDestination | null>(initialDestination);

  /**
   * Has the visitor edited this field yet?
   *
   * Both fields arrive pre-filled — Location from the resolved destination,
   * Attraction from `?q=` on a shared link — and both prefills are longer than
   * the three-character minimum, so without this every listing page load would
   * fire a suggestion request for a dropdown nobody opened. Suggestions are a
   * response to typing, not to arriving.
   */
  const [locationTouched, setLocationTouched] = useState(false);
  const [queryTouched, setQueryTouched] = useState(false);

  /**
   * "City is required", raised by touching the attraction field first.
   *
   * Lives on the LOCATION field, not the attraction one: the attraction field
   * is not at fault, and the fix is one field to the left. Cleared as soon as
   * the visitor edits the Location box, so it never outlives the problem.
   */
  const [cityError, setCityError] = useState(false);

  /**
   * The attraction field is a DEPENDENT field: with no destination pinned there
   * is no city to scope a product search to, and an unscoped one would offer
   * products from anywhere. Blocked rather than hidden so the dependency is
   * visible before it is attempted.
   */
  const attractionBlocked = selectedCity === null;

  const [cities, setCities] = useState<AttractionCityRecord[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [products, setProducts] = useState<AttractionSummary[]>([]);
  const [searching, setSearching] = useState(false);

  // ── Destinations: debounced, tier-ranked by the service ──────────────────
  // Ranking lives in `attractions-service` (`catalogue/services/tiered-rank.ts`)
  // for BOTH fields, so the tier tables exist once rather than once per field —
  // which is what BCP-2054 §3 asks for. That also makes the Location field
  // behave like every other vertical's (remote, debounced, min-3), the
  // consistency BCP-2055 rule 4 requires.
  const cityAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = location.trim();
    if (!locationTouched || q.length < MIN_QUERY_LEN) {
      cityAbortRef.current?.abort();
      setCities([]);
      setCitiesLoading(false);
      return;
    }

    setCitiesLoading(true);
    const timer = setTimeout(async () => {
      cityAbortRef.current?.abort();
      const controller = new AbortController();
      cityAbortRef.current = controller;
      const rows = await suggestCities(q, SUGGESTION_LIMIT, controller.signal);
      // A late response from an aborted request must not repopulate the menu.
      if (controller.signal.aborted) return;
      setCities(rows);
      setCitiesLoading(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [location, locationTouched]);

  const cityOptions: ComboOption[] = useMemo(
    () =>
      cities.map((c) => ({
        // The canonical id is the option's identity wherever it exists, so two
        // destinations sharing a name (Las Vegas NV / NM — both real, both in
        // the catalogue) stay two distinct options.
        id: c.cityId !== null && c.cityId !== undefined ? `id:${c.cityId}` : c.citySlug || c.city,
        value: c.city,
        label: c.city,
        // Region + country beneath the name. NO product count: it is inventory
        // bookkeeping, not something that helps a visitor pick a destination.
        hint: cityRegionLine(c),
        type: 'city' as const,
      })),
    [cities],
  );

  // ── Attractions: debounced live search, scoped to the destination ─────────
  // The in-flight request is aborted on every keystroke so a slow early query
  // cannot land after a later one and repopulate the menu with stale results.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (attractionBlocked || !queryTouched || q.length < MIN_QUERY_LEN) {
      abortRef.current?.abort();
      setProducts([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const rows = await suggestAttractions(
        q,
        SUGGESTION_LIMIT,
        // The destination goes to the SERVICE as a query param — the filtering
        // happens in Mongo, not here. Filtering a global top-5 in the browser
        // would quietly drop rows and under-fill the dropdown.
        selectedCity
          ? {
              // The canonical id first — see `suggestAttractions`. A
              // same-named second city cannot leak in through it.
              cityId: selectedCity.cityId ?? undefined,
              city: selectedCity.city,
              citySlug: selectedCity.citySlug,
            }
          : {},
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setProducts(rows);
      setSearching(false);
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `selectedCity` is a dependency on purpose: changing destination must
    // re-run the open search rather than leave the previous city's products up.
  }, [query, queryTouched, selectedCity, attractionBlocked]);

  const productOptions: ComboOption[] = useMemo(
    () =>
      products.map((p) => ({
        id: p.id,
        value: p.title,
        label: p.title,
        // The product's own photo. `toSummary` already falls back to the
        // shared `no-image.png` path when the supplier gave none, and the row
        // falls back again if the URL itself fails to load.
        image: p.image,
        // Name only — no price, rating, duration or city sub-line. The row
        // answers "which attraction did you mean", and the detail page answers
        // everything else.
        type: 'attraction' as const,
      })),
    [products],
  );

  /**
   * Location edits, typed or cleared, go through here.
   *
   * Editing away from the pinned destination unpins it, so the attraction
   * field cannot stay scoped to a city the field no longer names. That also
   * re-blocks the attraction field, which is why the dependent state is
   * cleared alongside it.
   */
  const changeLocation = (value: string) => {
    setLocationTouched(true);
    setLocation(value);
    setSelectedCity((current) => {
      if (current && sameCity(current.city, value)) return current;
      abortRef.current?.abort();
      setQuery('');
      setQueryTouched(false);
      setProducts([]);
      return null;
    });
    // The visitor is answering the question the error asked.
    setCityError(false);
  };

  /**
   * × on Location. Same effect as deleting the text by hand, plus forgetting
   * the shared "last searched city" (`theatre-searches-city`) the way the
   * Sports clear does. Otherwise `CityAutofillSync` would re-seed the city the
   * visitor just removed on their next visit.
   */
  const clearLocation = () => {
    changeLocation('');
    try {
      localStorage.removeItem('theatre-searches-city');
    } catch {
      /* localStorage disabled — nothing to do */
    }
  };

  /**
   * × on Attraction: empty the field, drop its suggestions, and remove an
   * APPLIED `?q=` so the listing resets straight away instead of waiting for
   * another Search. Only `q` is removed: the destination, filters and sort in
   * the URL stay as they were. Text that was typed but never searched is not
   * in the URL, so clearing it navigates nowhere.
   *
   * The URL is read here, in the click handler, rather than via
   * `useSearchParams`: it is only needed at the moment of the click.
   */
  const clearQuery = () => {
    abortRef.current?.abort();
    setQuery('');
    setProducts([]);
    setSearching(false);

    const params = new URLSearchParams(window.location.search);
    if (!params.has('q')) return;
    params.delete('q');
    const qs = params.toString();
    router.push(qs ? `/attractions-experiences?${qs}` : '/attractions-experiences');
  };

  /**
   * Run the search.
   *
   * The canonical `cityId` is written to the URL whenever a destination was
   * PICKED, and it is what the listing filters and facets on. When the visitor
   * typed a name without picking, only the name goes out — the listing page
   * then tries to resolve it and, failing that, says so. Guessing an id here
   * from partial text would pin the results to a city nobody chose.
   */
  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const typed = location.trim();

    // Searching with no destination is the same unmet dependency the attraction
    // field enforces — say so here too rather than navigating to a page that
    // would have to guess.
    if (!typed) {
      setCityError(true);
      return;
    }

    const params = new URLSearchParams();
    const pinned = selectedCity && sameCity(selectedCity.city, typed) ? selectedCity : null;

    /*
     * ONE location parameter, and it is readable.
     *
     * A picked destination contributes its unique `urlSlug` (`london`,
     * `las-vegas-new-mexico`); the listing page resolves that back to the
     * canonical `cityId` server-side, so the id stays authoritative without
     * ever appearing in the address bar. A destination that was typed but not
     * picked can only contribute the raw text, which the page then tries to
     * resolve — and names as unrecognised if it cannot.
     */
    params.set('city', pinned?.urlSlug || pinned?.citySlug || typed);
    if (query.trim()) params.set('q', query.trim());

    router.push(`/attractions-experiences?${params.toString()}`);
  };

  /**
   * Choosing a destination pins it for the attraction field. It does NOT search
   * on its own — the visitor may still want to name an attraction first, and
   * BCP-2055 rule 3 has them press Search.
   */
  const selectCity = (option: ComboOption) => {
    const record = cities.find(
      (c) =>
        (c.cityId !== null && c.cityId !== undefined ? `id:${c.cityId}` : c.citySlug || c.city) ===
        option.id,
    );
    setSelectedCity({
      cityId: record?.cityId ?? null,
      city: record?.city ?? option.value,
      citySlug: record?.citySlug,
      urlSlug: record?.urlSlug,
      region: record?.region,
      country: record?.country,
      countrySlug: record?.countrySlug,
      attractionCount: record?.attractionCount,
    });
    /*
     * Changing destination invalidates EVERYTHING the attraction field holds.
     *
     * "Tower" typed under London is not a Paris search, and its suggestions are
     * London product rows carrying London uuids — leaving either in place would
     * let a visitor open a London product from a Paris search, or submit
     * `?city=paris&q=Tower` and wonder why Paris has no Tower Bridge. The
     * in-flight request is aborted too, so a slow London response cannot land
     * after the switch and repopulate the menu.
     */
    abortRef.current?.abort();
    setQuery('');
    setQueryTouched(false);
    setProducts([]);
    setSearching(false);
    setCityError(false);
  };

  /**
   * Picking a product from the menu goes STRAIGHT to it rather than running a
   * search that would return the one row the user already chose. Opens in a
   * new tab, like the attraction cards, so the listing stays put.
   */
  const openProduct = (option: ComboOption): boolean => {
    const product = products.find((p) => p.id === option.id);
    if (!product) return false;
    window.open(buildAttractionUrl(product), '_blank', 'noopener,noreferrer');
    return true;
  };

  return (
    <section className="atr-container" style={{ paddingTop: 24 }}>
      <div className="atr-hero atr-ticket">
        <div className="atr-ticket__content">
        {/* Plain <img>: the hero is a full-bleed decorative backdrop, so it wants
            object-fit cover on a sized box rather than next/image's layout.

            STATICALLY IMPORTED rather than referenced by URL. The `IMG` prefix
            every other image uses points at the CMS host, which only serves
            files that have been uploaded to it — a new asset committed here
            404s there until someone does that by hand. A static import is
            bundled into `/_next/static/media/…`, which `assetPrefix` already
            rewrites correctly on every deployment, so the image ships with the
            app and needs no upload and no env var. */}
        {tenantHero ? (
          <img className="atr-hero__img" src={tenantHero} alt="" aria-hidden="true" />
        ) : (
          // No image of the tenant's own: its brand gradient, never BookItPlease's photo.
          <div className="atr-hero__img brand-hero-fallback--dark" aria-hidden="true" />
        )}
        <div className="atr-hero__scrim" />
        <div className="atr-hero__inner">
          <h1 className="atr-hero__title">Find Amazing Places to Visit</h1>

          <form className="atr-searchbar" onSubmit={submit} role="search">
            <SearchCombobox
              id="atr-location"
              ariaLabel="Search by city"
              placeholder="Search by city"
              icon={<PinIcon />}
              value={location}
              onChange={changeLocation}
              onClear={clearLocation}
              clearLabel="Clear location"
              options={cityOptions}
              loading={citiesLoading}
              minChars={MIN_QUERY_LEN}
              emptyLabel={NO_RESULTS}
              onSelect={selectCity}
              onEnterWithoutSelection={submit}
              invalid={cityError}
              describedBy={cityError ? 'atr-location-error' : undefined}
              // Rendered inside the Location field, beneath its input — the
              // message belongs with the field it is about.
              error={cityError ? CITY_REQUIRED : undefined}
              errorId="atr-location-error"
            />

            <SearchCombobox
              id="atr-query"
              ariaLabel="Search by Attractions/Experiences"
              placeholder="Search by Attractions/Experiences"
              icon={<SearchIcon />}
              value={query}
              onChange={(value) => {
                setQueryTouched(true);
                setQuery(value);
              }}
              options={productOptions}
              loading={searching}
              minChars={MIN_QUERY_LEN}
              onSelect={openProduct}
              onClear={clearQuery}
              clearLabel="Clear attraction search"
              emptyLabel={NO_RESULTS}
              onEnterWithoutSelection={submit}
              blocked={attractionBlocked}
              // Touching the dependent field explains the dependency on the
              // field that owns it, rather than letting someone type a whole
              // attraction name and only then be told it was never possible.
              onBlockedInteraction={() => setCityError(true)}
            />

            <button
              type="submit"
              className="atr-btn atr-btn-primary"
              style={{ padding: '14px 30px' }}
            >
              Search
            </button>
          </form>
        </div>
       </div>
      </div>
    </section>
  );
}
